import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { aulaService, catalogService } from '../../shared/services';
import { AdvancedVideoPlayer } from '../../shared/components/AdvancedVideoPlayer';
import { FileText, Download, ChevronRight, ChevronDown, PlayCircle, CheckCircle, Circle, X, ArrowRight } from 'lucide-react';
import { useRef, useState } from 'react';
import type { Modulo } from '../../shared/types';

type TabType = 'overview' | 'qa' | 'notes' | 'announcements' | 'reviews' | 'tools';

export function LessonPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const playerProgressRef = useRef(0);
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [showSidebar, setShowSidebar] = useState(true);

  const { data: aula, isLoading } = useQuery({
    queryKey: ['aula', id],
    queryFn: () => aulaService.getAula(id!),
    enabled: !!id,
  });

  // Buscar curso completo para mostrar na sidebar
  const courseId = (aula as (typeof aula & { cursoId?: string }) | undefined)?.cursoId || aula?.moduloId;

  const { data: curso } = useQuery({
    queryKey: ['curso-completo', courseId],
    queryFn: () => catalogService.getCurso(courseId!),
    enabled: !!courseId,
  });

  const startMutation = useMutation({
    mutationFn: (aulaId: string) => aulaService.startAula(aulaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aula', id] });
    },
  });

  const updatePositionMutation = useMutation({
    mutationFn: ({ aulaId, posicao }: { aulaId: string; posicao: number }) =>
      aulaService.updatePosition(aulaId, posicao),
  });

  const completeMutation = useMutation({
    mutationFn: ({ aulaId, posicao }: { aulaId: string; posicao: number }) =>
      aulaService.completeAula(aulaId, posicao),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aula', id] });
      queryClient.invalidateQueries({ queryKey: ['curso-completo'] });
      queryClient.invalidateQueries({ queryKey: ['progresso-resumo'] });
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleProgress = (state: any) => {
    playerProgressRef.current = state.playedSeconds;
    
    // Atualiza posição a cada 10 segundos
    if (Math.floor(state.playedSeconds) % 10 === 0 && id) {
      updatePositionMutation.mutate({ aulaId: id, posicao: state.playedSeconds });
    }
  };

  const handleEnded = () => {
    if (id && aula?.progresso?.status !== 'concluida') {
      completeMutation.mutate({ aulaId: id, posicao: playerProgressRef.current });
    }
  };

  const handleStart = () => {
    if (id && !aula?.progresso) {
      startMutation.mutate(id);
    }
  };

  const toggleModule = (moduleId: string) => {
    const newExpanded = new Set(expandedModules);
    if (newExpanded.has(moduleId)) {
      newExpanded.delete(moduleId);
    } else {
      newExpanded.add(moduleId);
    }
    setExpandedModules(newExpanded);
  };

  const getAulaStatus = (aulaId: string): 'current' | 'completed' | 'not-started' => {
    // Simular status baseado na aula atual
    if (aulaId === id) return 'current';
    // TODO: Verificar progresso real da aula
    return 'not-started';
  };

  const calculateModuleProgress = (modulo: Modulo) => {
    const total = modulo.aulas.length;
    const completed = modulo.aulas.filter(a => getAulaStatus(a.id) === 'completed').length;
    const totalMinutes = modulo.aulas.reduce((acc, a) => acc + (a.duracaoSeg || 0), 0) / 60;
    return { completed, total, totalMinutes: Math.round(totalMinutes) };
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '0m';
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!aula) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Aula não encontrada</h2>
        </div>
      </div>
    );
  }

  const videoUrl = aula.midias?.find((m) => m.tipo === 'video')?.url;

  const todasAulas = curso?.modulos?.flatMap((modulo) => modulo.aulas ?? []) ?? [];
  const indiceAtual = todasAulas.findIndex((item) => item.id === id);
  const proximaAula = indiceAtual >= 0 ? todasAulas[indiceAtual + 1] : undefined;
  const aulaConcluida = aula.progresso?.status === 'concluida';
  const percentualAula = aula.progresso?.percentual ?? 0;
  const podeConcluir = aulaConcluida
    || aula.tipo !== 'video'
    || !aula.duracaoSeg
    || percentualAula >= 90
    || (aula.progresso?.ultimaPosicaoSeg ?? 0) >= (aula.duracaoSeg ?? 0) * 0.9;

  const concluirAula = () => {
    if (!id) return;
    completeMutation.mutate({
      aulaId: id,
      posicao: Math.max(playerProgressRef.current, aula.progresso?.ultimaPosicaoSeg ?? 0),
    });
  };

  const tabs = [
    { id: 'overview' as TabType, label: 'Visão geral' },
    { id: 'qa' as TabType, label: 'Perguntas e respostas' },
    { id: 'notes' as TabType, label: 'Observações' },
    { id: 'announcements' as TabType, label: 'Anúncios' },
    { id: 'reviews' as TabType, label: 'Avaliações' },
    { id: 'tools' as TabType, label: 'Ferramentas de aprendizado' },
  ];

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Header com título e botão fechar */}
      <div className="bg-gray-900 border-b border-gray-800 px-6 py-3 flex items-center justify-between">
        <Link 
          to={curso ? `/cursos/${curso.slug}` : '/catalogo'} 
          className="text-white hover:text-gray-300 flex items-center gap-2"
        >
          <X size={20} />
          <span className="text-sm font-medium">{curso?.titulo || 'Voltar'}</span>
        </Link>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-400">
            Seu progresso
          </div>
          <div className="text-white font-medium">
            {curso?.progresso?.percentual || 0}%
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Conteúdo principal */}
        <div className="flex-1 overflow-y-auto">
          {/* Player de vídeo */}
          {aula.tipo === 'video' && !videoUrl && (
            <div className="bg-gray-800 text-gray-200 px-8 py-16 text-center">
              <p className="text-lg font-medium">O vídeo desta aula ainda não foi publicado.</p>
              <p className="text-sm text-gray-400 mt-2">A navegação da trilha já está disponível. O arquivo entra aqui quando for liberado.</p>
            </div>
          )}

          {aula.tipo === 'video' && videoUrl && (
            <AdvancedVideoPlayer
              videoUrl={videoUrl}
              qualities={[
                { label: '1080p', url: videoUrl.replace('.mp4', '_1080p.mp4'), height: 1080 },
                { label: '720p', url: videoUrl.replace('.mp4', '_720p.mp4'), height: 720 },
                { label: '480p', url: videoUrl.replace('.mp4', '_480p.mp4'), height: 480 },
              ]}
              onStart={handleStart}
              onProgress={handleProgress}
              onEnded={handleEnded}
              startPosition={aula.progresso?.ultimaPosicaoSeg}
            />
          )}

          {/* Ação de conclusão */}
          <div className="bg-white border-b border-gray-200 px-8 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm">
              {aulaConcluida ? (
                <>
                  <CheckCircle className="text-green-600" size={20} />
                  <span className="font-medium text-green-800">Aula concluída</span>
                </>
              ) : (
                <>
                  <Circle className="text-gray-400" size={20} />
                  <span className="text-gray-600">
                    {aula.tipo === 'video' && aula.duracaoSeg
                      ? `Assista pelo menos 90% da aula para concluir (${Math.round(percentualAula)}%).`
                      : 'Marque esta aula como concluída para avançar.'}
                  </span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!aulaConcluida && (
                <button
                  type="button"
                  onClick={concluirAula}
                  disabled={!podeConcluir || completeMutation.isPending}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {completeMutation.isPending ? 'Concluindo...' : 'Concluir aula'}
                </button>
              )}
              {proximaAula ? (
                <Link to={`/aulas/${proximaAula.id}`} className="btn-secondary inline-flex items-center gap-2">
                  Próxima aula <ArrowRight size={16} />
                </Link>
              ) : (
                <Link to={curso ? `/cursos/${curso.id}` : '/catalogo'} className="btn-secondary">
                  Voltar ao curso
                </Link>
              )}
            </div>
          </div>

          {/* Tabs de navegação */}
          <div className="bg-white border-b border-gray-200">
            <div className="flex gap-8 px-8 overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-gray-900 text-gray-900'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Conteúdo das tabs */}
          <div className="bg-white p-8">
            {activeTab === 'overview' && (
              <div className="max-w-4xl">
                <h1 className="text-2xl font-bold text-gray-900 mb-6">{aula.titulo}</h1>

                {/* Conteúdo em texto/markdown */}
                {aula.conteudo?.html && (
                  <div
                    className="prose max-w-none mb-8"
                    dangerouslySetInnerHTML={{ __html: aula.conteudo.html }}
                  />
                )}

                {/* Resumo */}
                {aula.conteudo?.resumo && (
                  <div className="mb-8">
                    <h2 className="text-lg font-semibold text-gray-900 mb-3">Resumo</h2>
                    <p className="text-gray-700">{aula.conteudo.resumo}</p>
                  </div>
                )}

                {/* Anexos */}
                {aula.conteudo?.anexos && aula.conteudo.anexos.length > 0 && (
                  <div className="mt-8">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Materiais Complementares</h2>
                    <div className="space-y-3">
                      {aula.conteudo.anexos.map((anexo: any) => (
                        <a
                          key={anexo.id}
                          href={anexo.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
                        >
                          <FileText size={24} className="text-gray-600" />
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{anexo.titulo}</p>
                            <p className="text-sm text-gray-500">
                              {(anexo.tamanho / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                          <Download size={20} className="text-gray-400" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'qa' && (
              <div className="max-w-4xl">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Perguntas e respostas</h2>
                <p className="text-gray-600">Nenhuma pergunta ainda. Seja o primeiro a perguntar!</p>
              </div>
            )}

            {activeTab === 'notes' && (
              <div className="max-w-4xl">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Observações</h2>
                <p className="text-gray-600">Você ainda não fez observações nesta aula.</p>
              </div>
            )}

            {activeTab === 'announcements' && (
              <div className="max-w-4xl">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Anúncios</h2>
                <p className="text-gray-600">Nenhum anúncio no momento.</p>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div className="max-w-4xl">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Avaliações</h2>
                <p className="text-gray-600">Este curso ainda não possui avaliações.</p>
              </div>
            )}

            {activeTab === 'tools' && (
              <div className="max-w-4xl">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Ferramentas de aprendizado</h2>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Programe um horário para o aprendizado</h3>
                  <p className="text-gray-600 text-sm">
                    Alguns minutos de estudo por dia valem a pena. Pesquisas mostram que os alunos que fazem do estudo um hábito têm maior probabilidade de alcançar suas metas. Reserve um tempo para estudar e receba lembretes usando seu programador de aprendizado.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar com conteúdo do curso */}
        {showSidebar && curso && (
          <div className="w-96 bg-white border-l border-gray-200 overflow-y-auto flex-shrink-0">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 z-10">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-gray-900">Conteúdo do curso</h3>
                <button
                  onClick={() => setShowSidebar(false)}
                  className="text-gray-600 hover:text-gray-900"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="text-sm text-gray-600">
                {curso.modulos.reduce((acc: number, m: Modulo) => acc + m.aulas.length, 0)} aulas
              </div>
            </div>

            <div className="p-4 space-y-2">
              {curso.modulos.map((modulo: Modulo) => {
                const progress = calculateModuleProgress(modulo);
                const isExpanded = expandedModules.has(modulo.id);

                return (
                  <div key={modulo.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleModule(modulo.id)}
                      className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors"
                    >
                      <div className="flex items-center gap-2 flex-1 text-left">
                        {isExpanded ? (
                          <ChevronDown size={20} className="text-gray-600" />
                        ) : (
                          <ChevronRight size={20} className="text-gray-600" />
                        )}
                        <div>
                          <div className="font-semibold text-gray-900 text-sm">
                            Seção {modulo.ordem}: {modulo.titulo}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            {progress.completed}/{progress.total} | {progress.totalMinutes}m
                          </div>
                        </div>
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="bg-white">
                        {modulo.aulas.map((aulaItem: any) => {
                          const status = getAulaStatus(aulaItem.id);
                          const isCurrent = aulaItem.id === id;

                          return (
                            <button
                              key={aulaItem.id}
                              onClick={() => navigate(`/aulas/${aulaItem.id}`)}
                              className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors border-t border-gray-100 ${
                                isCurrent ? 'bg-blue-50' : ''
                              }`}
                            >
                              <div className="flex-shrink-0">
                                {status === 'completed' ? (
                                  <CheckCircle size={20} className="text-green-600" />
                                ) : status === 'current' ? (
                                  <PlayCircle size={20} className="text-blue-600" />
                                ) : (
                                  <Circle size={20} className="text-gray-400" />
                                )}
                              </div>
                              <div className="flex-1 text-left">
                                <div className={`text-sm ${isCurrent ? 'font-semibold text-blue-600' : 'text-gray-900'}`}>
                                  {aulaItem.ordem}. {aulaItem.titulo}
                                </div>
                                <div className="text-xs text-gray-600 mt-1 flex items-center gap-2">
                                  <PlayCircle size={12} />
                                  {formatDuration(aulaItem.duracaoSeg)}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Botão para reabrir sidebar */}
        {!showSidebar && (
          <button
            onClick={() => setShowSidebar(true)}
            className="fixed right-4 top-20 bg-white border border-gray-300 rounded-lg px-4 py-2 shadow-lg hover:bg-gray-50 text-sm font-medium text-gray-900"
          >
            Conteúdo do curso
          </button>
        )}
      </div>
    </div>
  );
}
